import React, { useEffect, useState } from 'react'
import { InputGroup, Intent } from '@blueprintjs/core'
import { Card } from '../ui/card'
import { IconNames } from '@blueprintjs/icons'
import { ConversationList } from './ConversationList'
import type { Conversation } from '../../types/copilot'
import { Button } from '../ui/button'

interface ConversationSidebarProps {
    conversations: Conversation[]
    currentConversationId?: number | null
    onNewConversation: () => void
    onConversationSelect: (conversationId: number) => void
    onConversationUpdate: (
        conversationId: number,
        update: { title?: string; is_archived?: boolean },
    ) => void
    onConversationDelete: (conversationId: number) => void
    onRefreshConversations: () => void
}

export function ConversationSidebar({
    conversations,
    currentConversationId,
    onNewConversation,
    onConversationSelect,
    onConversationUpdate,
    onConversationDelete,
    onRefreshConversations,
}: ConversationSidebarProps) {
    const [searchTerm, setSearchTerm] = useState('')
    const [showArchived, setShowArchived] = useState(false)

    useEffect(() => {
        onRefreshConversations()
    }, [])

    const filteredConversations = conversations.filter((conversation) => {
        const matchesSearch =
            conversation.title?.toLowerCase().includes(searchTerm.toLowerCase()) || false
        const matchesArchived = showArchived || !conversation.is_archived
        return matchesSearch && matchesArchived
    })

    const activeConversations = filteredConversations.filter((conv) => !conv.is_archived)
    const archivedConversations = filteredConversations.filter((conv) => conv.is_archived)

    return (
        <div className="w-80 bg-gray-50 border-r border-gray-200 flex flex-col h-full">
            {/* Header */}
            <div className="px-4 pb-4 pt-3 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-medium">Conversations</h2>
                    <Button
                        onClick={onNewConversation}
                        small
                        intent={Intent.PRIMARY}
                        icon={IconNames.PLUS}
                        text="New Chat"
                    />
                </div>

                {/* Search */}
                <InputGroup
                    type="text"
                    placeholder="Search conversations..."
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setSearchTerm(e.target.value)
                    }
                    leftIcon={IconNames.SEARCH}
                    className="mb-2"
                />

                {/* Filters */}
                <div className="flex items-center space-x-2">
                    <Button
                        intent={!showArchived ? Intent.PRIMARY : Intent.NONE}
                        small
                        outlined={showArchived}
                        onClick={() => setShowArchived(false)}
                        text="Active"
                    />
                    <Button
                        intent={showArchived ? Intent.PRIMARY : Intent.NONE}
                        small
                        outlined={!showArchived}
                        onClick={() => setShowArchived(true)}
                        text="Archived"
                    />
                </div>
            </div>

            {/* Conversation List */}
            <div className="flex-1 overflow-y-auto py-3">
                {filteredConversations.length === 0 ? (
                    <Card className="p-4 text-center text-gray-500">
                        {searchTerm ? (
                            <p>No conversations found matching "{searchTerm}"</p>
                        ) : showArchived ? (
                            <p>No archived conversations</p>
                        ) : (
                            <p>No conversations yet. Start a new chat!</p>
                        )}
                    </Card>
                ) : (
                    <div className="space-y-3">
                        {!showArchived && activeConversations.length > 0 && (
                            <div>
                                <h3 className="border-l-4 border-transparent px-4 text-xs font-medium text-gray-500 tracking-wider uppercase mb-3">
                                    Recent
                                </h3>
                                <ConversationList
                                    conversations={activeConversations}
                                    currentConversationId={currentConversationId}
                                    onConversationSelect={onConversationSelect}
                                    onConversationUpdate={onConversationUpdate}
                                    onConversationDelete={onConversationDelete}
                                />
                            </div>
                        )}

                        {showArchived && archivedConversations.length > 0 && (
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-3 px-1">
                                    Archived
                                </h3>
                                <ConversationList
                                    conversations={archivedConversations}
                                    currentConversationId={currentConversationId}
                                    onConversationSelect={onConversationSelect}
                                    onConversationUpdate={onConversationUpdate}
                                    onConversationDelete={onConversationDelete}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
